import React from 'react'
import { Alert, Button, Table } from '@surecompanies/core_components'
import { Empty } from 'antd'
import isNil from 'lodash/isNil'
import { ROW_CONTROL_SELECTOR } from '../../constants'
import { BillingProfileRecord } from '../../api/dto/client-billing-profile.dto'
import { BillingProfileStatus } from './types'
import { ShowingCount } from '../shared/finance.styles'
import {
  PageContent,
  ShowingBar,
  TableCard,
} from './ClientBillingProfileManagement.styles'
import { BillingProfileModal } from './ClientBillingProfileModal'
import { RemoveCompanyModal } from './RemoveCompanyModal'
import { NewCompanyModal } from './NewCompanyModal'
import { ExportModal } from './ExportModal'
import { PROFILE_EXPORT_SCOPES } from './bulkUpload.excel'
import { BulkUploadModal } from './BulkUploadModal'
import { GlobalCCEmailsModal } from './GlobalCCEmailsModal'
import { HubspotSyncFlow } from './HubspotSyncFlow'
import { BillingProfileToolbar } from './BillingProfileToolbar'
import { useBillingProfileColumns } from './useBillingProfileColumns'
import { useBillingProfileTable } from './useClientBillingProfileTable'
import {
  PAGE_SIZE_OPTIONS,
  toBillingProfileSort,
} from './useBillingProfileFilters'
import { useHubspotSync } from './useHubspotSync'
import { usePermission } from 'enrollment-platform/rbac/usePermission'
import { RbacActions, RbacSubjects } from 'enrollment-platform/rbac/constants'

const BillingProfileTable = () => {
  const {
    search,
    setSearch,
    statusFilter,
    paymentTypeFilter,
    sort,
    page,
    pageSize,
    handlePageChange,
    records,
    total,
    isLoading,
    isError,
    stats,
    modalRecord,
    modalOpen,
    removeRecord,
    createModalOpen,
    uploadModalOpen,
    exportModalOpen,
    globalCcOpen,
    isExporting,
    updatedRowId,
    hasNarrowedList,
    handleStatusFilter,
    handlePaymentTypeFilter,
    handleSortChange,
    handleClearFilters,
    handleOpenCreate,
    handleCloseCreate,
    handleOpenUpload,
    handleCloseUpload,
    handleOpenExport,
    handleCloseExport,
    handleExport,
    handleOpenGlobalCc,
    handleCloseGlobalCc,
    handleOpenUpdate,
    handleToggleStatus,
    handleOpenRemove,
    handleCloseRemove,
    handleConfirmRemove,
    handleModalClose,
  } = useBillingProfileTable()

  const sync = useHubspotSync()

  const { allowed: canWriteBillingProfile } = usePermission(
    RbacActions.Write,
    RbacSubjects.BillingProfile
  )

  const columns = useBillingProfileColumns({
    canWriteBillingProfile,
    sort,
    onEdit: handleOpenUpdate,
    onToggleStatus: handleToggleStatus,
    onRemove: handleOpenRemove,
  })

  const emptyState = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        hasNarrowedList
          ? 'No companies match these filters'
          : 'No billing profiles yet'
      }
    >
      {hasNarrowedList ? (
        <Button size="small" onClick={handleClearFilters}>
          Clear filters
        </Button>
      ) : null}
    </Empty>
  )

  const getRowClassName = (record: BillingProfileRecord) =>
    [
      record.id === updatedRowId ? 'row-just-updated' : '',
      canWriteBillingProfile ? 'row-clickable' : '',
      record.status === BillingProfileStatus.Inactive ? 'row-inactive' : '',
    ]
      .filter(Boolean)
      .join(' ')

  // The whole row opens the profile, matching the design. Controls inside the
  // row own their own clicks, including the kebab's menu items: antd renders
  // the menu in a portal, but React still bubbles its clicks through here.
  const getRowProps = (record: BillingProfileRecord) =>
    canWriteBillingProfile
      ? {
          onClick: (event: React.MouseEvent<HTMLElement>) => {
            if ((event.target as HTMLElement).closest(ROW_CONTROL_SELECTOR))
              return
            // Reading a value by selecting it should not also open the profile.
            const selection = window.getSelection()
            if (!isNil(selection) && selection.toString().length > 0) return
            handleOpenUpdate(record)
          },
        }
      : {}

  return (
    <PageContent>
      <BillingProfileToolbar
        search={search}
        statusFilter={statusFilter}
        paymentTypeFilter={paymentTypeFilter}
        statusCounts={stats}
        matchCount={total}
        isExporting={isExporting}
        onSearchChange={setSearch}
        onStatusFilterChange={handleStatusFilter}
        onPaymentTypeFilterChange={handlePaymentTypeFilter}
        onOpenHubspotSync={sync.handleSyncClick}
        onOpenCreate={handleOpenCreate}
        onOpenUpload={handleOpenUpload}
        onExport={handleOpenExport}
        onOpenGlobalCc={handleOpenGlobalCc}
      />

      <HubspotSyncFlow state={sync} />

      {isError ? (
        <Alert
          type="error"
          message="Failed to load billing profiles. Please refresh the page."
          showIcon
        />
      ) : null}

      {total > 0 ? (
        <ShowingBar>
          Showing{' '}
          <ShowingCount>
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}
          </ShowingCount>{' '}
          of <ShowingCount>{total}</ShowingCount> billing profiles
        </ShowingBar>
      ) : null}

      <TableCard>
        <Table
          dataSource={records}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          rowClassName={getRowClassName}
          // One empty state, and the filters that emptied the table can be
          // cleared from it.
          locale={{ emptyText: emptyState }}
          onRow={getRowProps}
          onChange={(_pagination, _filters, sorter, { action }) => {
            if (action === 'sort')
              handleSortChange(toBillingProfileSort(sorter))
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: handlePageChange,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
          }}
          size="small"
          // Sum of the column widths, so the frozen column has nothing to
          // stretch into.
          scroll={{ x: 1230 }}
        />
      </TableCard>

      <BillingProfileModal
        open={modalOpen}
        record={modalRecord}
        onClose={handleModalClose}
      />

      <RemoveCompanyModal
        open={!isNil(removeRecord)}
        record={removeRecord}
        onRemove={handleConfirmRemove}
        onClose={handleCloseRemove}
      />

      <NewCompanyModal open={createModalOpen} onClose={handleCloseCreate} />

      <ExportModal
        open={exportModalOpen}
        isExporting={isExporting}
        scopes={PROFILE_EXPORT_SCOPES}
        onClose={handleCloseExport}
        onDownload={handleExport}
      />

      <BulkUploadModal open={uploadModalOpen} onClose={handleCloseUpload} />

      <GlobalCCEmailsModal open={globalCcOpen} onClose={handleCloseGlobalCc} />
    </PageContent>
  )
}

export { BillingProfileTable }
